import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Col, Container, Row, Card, CardBody } from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import {
  deleteClient,
  deleteInvoice,
  deleteQuote,
  getClientDetails,
  updateClient,
  getPayments,
  regeneratePaymentLink,
  sharePayment,
} from "../../slices/thunks";
import { useSelector, useDispatch } from "react-redux";
import TableContainer from "../../Components/Common/TableContainer";
import Loader from "../../Components/Common/Loader";
import { createSelector } from "reselect";
import ClientDetailsHeader from "Components/ClientDetails/ClientDetailsHeader";
import ClientDetailsFilter, {
  FilterParams,
} from "Components/ClientDetails/ClientDetailsFilter";
import { selectClientsState } from "slices/clients/reducer";
import { useLocation, useNavigate } from "react-router-dom";
import ClientInfoModal from "Components/Modals/ClientInfoModal";
import { ClientRecentActivity, EditClientFormParams } from "types/client.api";
import ManageClientModal from "Components/Client/ManageClientModal";
import DeleteItemModal from "Components/Modals/DeleteItemModal";
import HowCreateQuotesModal from "Components/Modals/HowCreateQuotesModal";
import { getStatusBadge } from "utils/getStatusBatch";
import { formatDate_DD_MMM_YYYY } from "utils/formatDates";
import { clearQuoteDetails } from "slices/quotes/reducer";
import { clearItemState } from "slices/items/reducer";
import SelectPresetQuoteModal from "Components/Modals/SelectPresetQuoteModal";
import ActionMenu from "Components/Common/ActionMenu";
import {
  clearInvoiceDetails,
  resetCreateInvoiceStatus,
  resetEditInvoiceStatus,
} from "slices/invoices/reducer";
import RestrictedAccessModal from "Components/Modals/RestrictedAccessModal";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import PageTabs from "Components/Common/PageTabs";
import { IconName } from "Components/Icons/Icon";
import { ErrorHandler, ErrorToast } from "utils/toast";
import CustomShareModal from "Components/Modals/CustomShareModal";

const ClientDetails = () => {
  const dispatch: any = useDispatch();
  const appDispatch = useAppDispatch();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [showHowToCreateQuotePopUp, setHowToCreateQuotePopUp] = useState(false);
  const [filterData, setFilterData] = useState<FilterParams | null>(null);
  const [showSelectPresetModal, setShowSelectPresetModal] = useState(false);
  const [showSubscriptionExpiredPopUp, setShowSubscriptionExpiredPopUp] =
    useState(false);
  const [showClientInfoModal, setShowClientInfoModal] = useState(false);

  const openClientInfoModal = () => setShowClientInfoModal(true);
  const closeClientInfoModal = () => setShowClientInfoModal(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const user = useAppSelector((state) => state.auth.user);
  const { payments, pagination, status, generatedLinkDetails } = useAppSelector(
    (state) => state.payment,
  );
  const [activeTab, setActiveTab] = useState<string>("1");

  useEffect(() => {
    if (activeTab === "2" && state?.id) {
      appDispatch(getPayments({ client_id: state.id }));
    }
  }, [activeTab, state?.id, appDispatch]);

  const hasNotSubscribed =
    user?.is_subscription_active === false && user.is_trial_period === false;
  const hasBankAccount = user?.hasBankAccountDetailAdded;
  const hasSignature = user?.hasSignatureAdded;

  const [restrictionModalData, setRestrictionModalData] = useState<{
    iconName: IconName;
    title: string;
    description: string;
    buttonText: string;
    onAction: () => void;
  }>({
    iconName: "" as IconName,
    title: "",
    description: "",
    buttonText: "",
    onAction: () => {},
  });

  const RESTRICTION_CONFIG = {
    SUBSCRIPTION_REQUIRED: {
      iconName: "SubscriptionRequired",
      title: "Subscription Required",
      description:
        "To continue using EaziQuote, please activate or renew your subscription.",
      buttonText: "Activate Subscription",
      onAction: () => {
        if (user?.is_team_member) {
          ErrorToast(
            "Only the account owner can manage subscriptions. Please contact your owner.",
          );
        } else {
          navigate("/subscribe-plan");
        }
      },
    },
    BANK_ACCOUNT_REQUIRED: {
      iconName: "Info",
      title: "Add Bank Details",
      description:
        "Including bank details makes it easier for clients to pay you without follow-ups.",
      buttonText: "Add Bank Details",
      onAction: () => navigate("/payments-and-invoicing"),
    },
    SIGNATURE_REQUIRED: {
      iconName: "Info",
      title: "Add Your Signature",
      description:
        "Adding a signature helps build trust and makes your quote feel complete and professional.",
      buttonText: "Add Signature",
      onAction: () =>
        navigate("/payments-and-invoicing", {
          state: {
            signatureMissing: true,
          },
        }),
    },
    BANK_AND_SIGNATURE_REQUIRED: {
      iconName: "Info",
      title: "Complete Your Setup",
      description:
        "Add your bank details and signature to ensure your quotes look professional and include payment information.",
      buttonText: "Complete Setup",
      onAction: () => navigate("/payments-and-invoicing"),
    },
  } as const;

  const clientSelector = createSelector(selectClientsState, (state) => ({
    clientDetails: state.clientDetails,
    isLoading: state.status.fetchById,
    error: state.errors.fetchById,
  }));

  const { clientDetails, isLoading, error } = useSelector(clientSelector);

  useEffect(() => {
    const fetchClientDetails = async () => {
      if (!state?.id) {
        navigate(-1);
        return;
      }

      try {
        await dispatch(getClientDetails(state.id)).unwrap();
      } catch (error) {
        console.error("Error fetching client details:", error);
        navigate(-1);
        ErrorToast("Client not found.");
      }
    };

    fetchClientDetails();
  }, [dispatch, state?.id, navigate]);

  const [deleteModal, setDeleteModal] = useState<{
    type: "quote" | "invoice" | "client";
    id: number;
  } | null>(null);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [showEditClientModal, setShowEditClientModal] = useState(false);

  const applyClientFilters = (
    items: ClientRecentActivity[],
    filters: FilterParams | null,
  ): ClientRecentActivity[] => {
    if (!filters) {
      return items;
    } else {
      return items.filter((item) => {
        const itemDate = new Date(item.created_at);

        // Filter by Start Date
        if (filters.startDate) {
          const start = new Date(filters.startDate);
          if (itemDate < start) return false;
        }

        // Filter by End Date
        if (filters.endDate) {
          const end = new Date(filters.endDate);
          // Add 23:59:59 so the full day is included
          end.setHours(23, 59, 59);
          if (itemDate > end) return false;
        }

        // Filter by Status
        if (filters.statuses && filters.statuses.length > 0) {
          if (!filters.statuses.includes(item.status.toLowerCase())) {
            return false;
          }
        }

        return true;
      });
    }
  };

  const goToReceiptDetails = (data: ClientRecentActivity) => {
    if (data.type === "quote") {
      navigate("/quote-details", { state: { id: data.id } });
    }
    if (data.type === "invoice") {
      navigate("/invoice-details", { state: { id: data.id } });
    }
  };

  const onDelete = () => {
    if (deleteModal?.type === "client") {
      if (clientDetails) {
        dispatch(deleteClient(deleteModal.id));
        navigate("/clients", { replace: true });
      }
    } else if (deleteModal?.type === "quote") {
      dispatch(deleteQuote(deleteModal.id));
    } else if (deleteModal?.type === "invoice") {
      dispatch(deleteInvoice(deleteModal.id));
    }
    setDeleteModal(null);
  };

  const filteredList = useMemo(() => {
    return applyClientFilters(
      clientDetails?.recent_activities || [],
      filterData,
    );
  }, [clientDetails?.recent_activities, filterData]);

  const handleRegenerateLink = useCallback(
    async (rowData: any) => {
      try {
        setShareModalOpen(true);
        await dispatch(regeneratePaymentLink({ id: rowData.id })).unwrap();
      } catch (error) {
        ErrorHandler(error);
        setShareModalOpen(false);
      }
    },
    [dispatch],
  );

  // Column
  const columns = useMemo(
    () => [
      {
        header: "TITLE",
        accessorKey: "title",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "QUOTE/INVOICE",
        accessorKey: "reference_number",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "AMOUNT",
        accessorKey: "price",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {"£" + cellProps.getValue().toFixed(2)}{" "}
          </span>
        ),
      },
      {
        header: "STATUS",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps: any) => getStatusBadge(cellProps?.getValue()),
      },
      {
        header: "CREATION DATE",
        accessorKey: "created_at",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {formatDate_DD_MMM_YYYY(cellProps.getValue()) ?? "-"}
          </span>
        ),
      },
      {
        header: "EXPIRY/DUE DATE",
        accessorKey: "created_at",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {formatDate_DD_MMM_YYYY(cellProps.getValue()) ?? "-"}
          </span>
        ),
      },

      {
        header: "ACTION",
        enableSorting: false,
        cell: (cellProps: any) => {
          const item = cellProps.row.original;

          return (
            <ActionMenu
              isEdit={item.is_editable}
              isDelete={item.is_editable}
              isView
              onView={() => {
                goToReceiptDetails(item);
              }}
              onDelete={() => {
                setDeleteModal({
                  type: item.type,
                  id: item.id,
                });
              }}
              onEdit={() => {
                if (item.type === "quote") {
                  dispatch(clearQuoteDetails());
                  dispatch(clearItemState());
                  navigate("/manage-quotes", {
                    state: { id: item.id },
                  });
                } else {
                  dispatch(clearQuoteDetails());
                  dispatch(clearInvoiceDetails());
                  dispatch(resetEditInvoiceStatus());
                  dispatch(resetCreateInvoiceStatus());
                  navigate("/manage-invoice", {
                    state: { id: item.id },
                  });
                }
              }}
            />
          );
        },
      },
    ],
    [],
  );

  const paymentColumns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue() ?? "-"}
          </span>
        ),
      },
      {
        header: "DATE",
        accessorKey: "payment_date",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {formatDate_DD_MMM_YYYY(cell.getValue()) ?? "-"}
          </span>
        ),
      },
      {
        header: "TYPE",
        accessorKey: "amount_type",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary text-capitalize">
            {cell.getValue() ?? "-"}
          </span>
        ),
      },
      {
        header: "AMOUNT",
        accessorKey: "amount_formatted",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {`£${cell.getValue()}`}
          </span>
        ),
      },
      {
        header: "ALLOCATED",
        accessorKey: "allocated",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {`£${cell.getValue()}`}
          </span>
        ),
      },
      {
        header: "CREDIT",
        accessorKey: "credit",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {`£${cell.getValue()}`}
          </span>
        ),
      },
      {
        header: "STATUS",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps: any) => getStatusBadge(cellProps.getValue()),
      },
      {
        header: "METHOD",
        accessorKey: "payment_method",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary text-capitalize">
            {cell.getValue() ?? "-"}
          </span>
        ),
      },
      {
        header: "ACTION",
        cell: (cell: any) => {
          const rowData = cell.row.original;
          return (
            <ActionMenu
              isView
              onView={() =>
                navigate("/view-payment", { state: { id: rowData.id } })
              }
              isDelete={false}
              isShare={
                rowData.status === "pending" || rowData.status === "failed"
                  ? true
                  : false
              }
              onShare={() => handleRegenerateLink(rowData)}
              isEdit={false}
            />
          );
        },
      },
    ],
    [handleRegenerateLink, navigate],
  );

  const handleCreateRecord = () => {
    navigate("/record-payment", { state: { client_id: clientDetails?.id } });
  };

  document.title = "Client Details | EaziQuote";

  if (isLoading === "loading" || !clientDetails) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <Loader error={error} />
      </Container>
    );
  }

  return (
    <React.Fragment>
      <HowCreateQuotesModal
        show={showHowToCreateQuotePopUp}
        onStartFromScratchClick={() => {
          setHowToCreateQuotePopUp(false);
          dispatch(clearQuoteDetails());
          dispatch(clearItemState());
          navigate("/manage-quotes", {
            state: {
              client_details_payload: clientDetails,
            },
          });
        }}
        onUsePresetClick={() => {
          setShowSelectPresetModal(true);
          setHowToCreateQuotePopUp(false);
        }}
        onCloseClick={() => {
          setHowToCreateQuotePopUp(false);
        }}
      />

      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={"Client Detail"}
            pageTitle={"Clients"}
            url="/clients"
          />
          <Row className="mb-4">
            <ClientDetailsHeader
              onClickEdit={() => {
                setShowEditClientModal(true);
              }}
              onclickDelete={() => {
                setDeleteModal({
                  type: "client",
                  id: clientDetails.id,
                });
              }}
              handleContactClick={() => {
                openClientInfoModal();
              }}
              clientData={clientDetails}
            />
          </Row>

          <PageTabs
            active={activeTab}
            onChange={setActiveTab}
            tabs={[
              { id: "1", label: "Recent Activity" },
              { id: "2", label: "Payments" },
            ]}
          />

          {activeTab === "1" && (
            <Row>
              <Col xxl={12}>
                <Card id="contactList" className="br-10 bg-white">
                  <CardBody className="pt-0">
                    <div>
                      <TableContainer
                        columns={columns}
                        data={filteredList || []}
                        isGlobalFilter={true}
                        customPageSize={clientDetails?.recent_activities.length}
                        divClass="table-responsive table-card mb-2 bg-white"
                        tableClass="align-middle table-nowrap table-borderless"
                        thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5 category-table"
                        theadClass="table-light"
                        SearchPlaceholder="Search quotes & invoices"
                        searchAlign="left"
                        isClientDetails={true}
                        onClientDetailFilterClick={() => {
                          setShowFilter(true);
                        }}
                        onNewQuoteClick={() => {
                          if (hasNotSubscribed) {
                            setShowSubscriptionExpiredPopUp(true);
                            setRestrictionModalData(
                              RESTRICTION_CONFIG.SUBSCRIPTION_REQUIRED,
                            );
                          } else if (!hasBankAccount && !hasSignature) {
                            setShowSubscriptionExpiredPopUp(true);
                            setRestrictionModalData(
                              RESTRICTION_CONFIG.BANK_AND_SIGNATURE_REQUIRED,
                            );
                          } else if (!hasBankAccount) {
                            setShowSubscriptionExpiredPopUp(true);
                            setRestrictionModalData(
                              RESTRICTION_CONFIG.BANK_ACCOUNT_REQUIRED,
                            );
                          } else if (!hasSignature) {
                            setShowSubscriptionExpiredPopUp(true);
                            setRestrictionModalData(
                              RESTRICTION_CONFIG.SIGNATURE_REQUIRED,
                            );
                          } else {
                            setHowToCreateQuotePopUp(true);
                          }
                        }}
                        showPagination={false}
                      />
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {activeTab === "2" && (
            <Row>
              <Col xxl={12}>
                <Card className="br-10 bg-white">
                  <CardBody className="pt-0">
                    <TableContainer
                      columns={paymentColumns}
                      data={payments || []}
                      isGlobalFilter={false}
                      divClass="table-responsive table-card mb-2 bg-white"
                      tableClass="align-middle table-nowrap table-borderless"
                      thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5 category-table"
                      theadClass="table-light"
                      customPageSize={pagination?.per_page}
                      currentPage={pagination?.current_page}
                      totalPages={pagination?.last_page}
                      toPage={pagination?.to}
                      fromPage={pagination?.from}
                      totalItems={pagination?.total}
                      isInitialLoading={
                        status.fetch === "loading" && !payments?.length
                      }
                      isPaginationLoading={
                        status.fetch === "loading" && payments?.length > 0
                      }
                      isPaymentDetails
                      isShowRecordPaymentButton
                      onCreatePaymentRecordClick={handleCreateRecord}
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>
      <ClientDetailsFilter
        show={showFilter}
        selectedFilter={filterData}
        onCloseClick={() => setShowFilter(false)}
        onApplyClick={(e) => {
          setFilterData(e);
          setShowFilter(false);
        }}
        onCanCelClick={() => {
          setFilterData(null);
          setShowFilter(false);
        }}
      />
      <ClientInfoModal
        isOpen={showClientInfoModal}
        onClose={closeClientInfoModal}
        client={clientDetails}
        onViewProfileClick={() => {
          navigate("/client-details", {
            state: { id: clientDetails.id },
          });
        }}
        isFromClientDetailsPage={true}
      />
      <ManageClientModal
        isOpen={showEditClientModal}
        toggleModal={() => {
          setShowEditClientModal((prev) => !prev);
        }}
        isEdit={true}
        initialData={clientDetails}
        onSubmit={(payload) => {
          dispatch(updateClient(payload as EditClientFormParams));
        }}
      />
      <DeleteItemModal
        show={deleteModal !== null}
        onDeleteClick={onDelete}
        onCloseClick={() => setDeleteModal(null)}
      />

      <SelectPresetQuoteModal
        isOpen={showSelectPresetModal}
        onClose={() => setShowSelectPresetModal(false)}
        onContinue={(template) => {
          console.log("Selected clientDetails", clientDetails);
          if (template === null) {
            setShowSelectPresetModal(false);
            return;
          }
          dispatch(clearQuoteDetails());
          dispatch(clearItemState());
          navigate("/manage-quotes", {
            state: { template, client_details_payload: clientDetails },
          });
          setShowSelectPresetModal(false);
        }}
      />
      <RestrictedAccessModal
        isOpen={showSubscriptionExpiredPopUp}
        onCancel={() => setShowSubscriptionExpiredPopUp(false)}
        iconName={restrictionModalData.iconName}
        title={restrictionModalData.title}
        description={restrictionModalData.description}
        buttonText={restrictionModalData.buttonText}
        onButtonClick={restrictionModalData.onAction}
      />

      <CustomShareModal
        isOpen={shareModalOpen}
        onClose={() => {
          setShareModalOpen(false);
          navigate(-1);
        }}
        title="Share Payment Link"
        shareUrl={generatedLinkDetails?.stripe_payment_link_url ?? ""}
        subject="Payment Link"
        isLoading={status.regenerateLink === "loading"}
        recipientEmail={generatedLinkDetails?.client_email ?? ""}
        message={
          generatedLinkDetails?.client_name
            ? `Hi ${generatedLinkDetails.client_name}, here is your payment link:\n\n${generatedLinkDetails.stripe_payment_link_url}\n\nThank you.`
            : `Here is your payment link:\n\n${
                generatedLinkDetails?.stripe_payment_link_url ?? ""
              }\n\nThank you.`
        }
        onSendEmail={() =>
          generatedLinkDetails?.id &&
          dispatch(sharePayment({ id: generatedLinkDetails.id }))
        }
        isSending={status.share === "loading"}
      />
    </React.Fragment>
  );
};

export default ClientDetails;
